/**
 * Bottom sheet for ticket regeneration options.
 * Allows choosing between image-only update or full regeneration.
 * Does NOT send to user - use SendSheet after regeneration.
 */

import { useState } from "react";
import { haptic } from "../../../../lib/telegram.ts";

export interface RegenerateOptions {
  imageOnly: boolean;
  sendToUser: boolean;
}

export interface RegenerateSheetProps {
  /** Is the sheet open */
  isOpen: boolean;
  /** Callback to close the sheet */
  onClose: () => void;
  /** Callback when regenerate is confirmed */
  onConfirm: (options: RegenerateOptions) => void;
  /** Is currently processing */
  isProcessing?: boolean;
  /** Color variant: light (default) or dark */
  variant?: "light" | "dark";
}

export function RegenerateSheet({
  isOpen,
  onClose,
  onConfirm,
  isProcessing = false,
  variant = "light",
}: RegenerateSheetProps) {
  const [imageOnly, setImageOnly] = useState(true);

  if (!isOpen) return null;

  const handleConfirm = () => {
    haptic("medium");
    // Always sendToUser: false - use SendSheet after regeneration
    onConfirm({ imageOnly, sendToUser: false });
  };

  const isDark = variant === "dark";

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 ${isDark ? "bg-black/60" : "bg-black/40"}`}
        onClick={onClose}
        aria-hidden
      />

      {/* Sheet */}
      <div
        className={`fixed inset-x-0 bottom-0 z-50 max-h-[85vh] overflow-y-auto rounded-t-[20px] shadow-[0_-4px_24px_rgba(0,0,0,0.12)] safe-area-bottom ${
          isDark ? "bg-slate-900" : "bg-[var(--app-bg)]"
        }`}
      >
        {/* Handle */}
        <div
          className={`sticky top-0 flex justify-center py-3 ${isDark ? "bg-slate-900" : "bg-[var(--app-bg)]"}`}
        >
          <div
            className={`w-10 h-1 rounded-full ${isDark ? "bg-white/20" : "bg-slate-200"}`}
            aria-hidden
          />
        </div>

        <div className="px-4 pb-6">
          <h3
            className={`text-[17px] font-semibold mb-1 ${isDark ? "text-white" : "text-slate-900"}`}
          >
            Перегенерация билетов
          </h3>
          <p
            className={`text-[14px] mb-4 ${isDark ? "text-white/50" : "text-slate-500"}`}
          >
            Выберите режим перегенерации
          </p>

          <div className="space-y-3 mb-6">
            {/* Image only option */}
            <button
              type="button"
              onClick={() => {
                haptic("light");
                setImageOnly(true);
              }}
              className={`w-full flex items-start gap-3 p-4 rounded-2xl text-left transition-colors ${
                imageOnly
                  ? "bg-teal-500/20 border-2 border-teal-500"
                  : isDark
                    ? "bg-white/5 border-2 border-transparent active:bg-white/10"
                    : "bg-slate-100 border-2 border-transparent active:bg-slate-200"
              }`}
            >
              <span
                className={`shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  imageOnly
                    ? "border-teal-500 bg-teal-500"
                    : isDark
                      ? "border-white/30 bg-transparent"
                      : "border-slate-300 bg-white"
                }`}
              >
                {imageOnly && (
                  <span className="w-2 h-2 rounded-full bg-white" />
                )}
              </span>
              <div>
                <div
                  className={`font-medium text-[15px] ${isDark ? "text-white" : "text-slate-900"}`}
                >
                  Обновить информацию
                </div>
                <div
                  className={`text-[13px] mt-0.5 ${isDark ? "text-white/50" : "text-slate-500"}`}
                >
                  Номера сохранятся, обновятся дата и место
                </div>
              </div>
            </button>

            {/* Full regeneration option */}
            <button
              type="button"
              onClick={() => {
                haptic("light");
                setImageOnly(false);
              }}
              className={`w-full flex items-start gap-3 p-4 rounded-2xl text-left transition-colors ${
                !imageOnly
                  ? "bg-amber-500/20 border-2 border-amber-500"
                  : isDark
                    ? "bg-white/5 border-2 border-transparent active:bg-white/10"
                    : "bg-slate-100 border-2 border-transparent active:bg-slate-200"
              }`}
            >
              <span
                className={`shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  !imageOnly
                    ? "border-amber-500 bg-amber-500"
                    : isDark
                      ? "border-white/30 bg-transparent"
                      : "border-slate-300 bg-white"
                }`}
              >
                {!imageOnly && (
                  <span className="w-2 h-2 rounded-full bg-white" />
                )}
              </span>
              <div>
                <div
                  className={`font-medium text-[15px] ${isDark ? "text-white" : "text-slate-900"}`}
                >
                  Полная перегенерация
                </div>
                <div
                  className={`text-[13px] mt-0.5 ${isDark ? "text-white/50" : "text-slate-500"}`}
                >
                  Новые номера и изображения
                </div>
              </div>
            </button>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isProcessing}
              className={`flex-1 min-h-[50px] rounded-xl text-[15px] font-medium disabled:opacity-50 ${
                isDark
                  ? "bg-white/10 text-white active:bg-white/15"
                  : "bg-slate-100 text-slate-700 active:bg-slate-200"
              }`}
            >
              Отмена
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={isProcessing}
              className={`flex-1 min-h-[50px] rounded-xl text-white text-[15px] font-medium active:opacity-90 disabled:opacity-50 flex items-center justify-center ${
                imageOnly ? "bg-teal-500" : "bg-amber-500"
              }`}
            >
              {isProcessing ? (
                <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                "Перегенерировать"
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
